import React, { useEffect, useMemo } from 'react';
import { partition } from 'lodash';
import { Stack } from '@fiverr-private/layout_components';
import { getContext } from '@fiverr-private/fiverr_context';
import { Layout } from '../../components/common/Layout';
import { containerPaddingXBySmBreakpoint } from '../../components/common/containerStylePaddingX.ve.css';
import { ExcludedSectionsNavBar, ExcludedSectionsSidebar } from '../../components/EditBrief/ExcludedSections';
import { IncludedSections } from '../../components/EditBrief/IncludedSections';
import { AppProvider, AppProviderProps, useBriefContext } from '../../common/BriefContext';
import { QueryProvider } from '../../common/queryClient';
import { Brief } from '../../types';

interface BriefEditorProps extends Omit<AppProviderProps, 'children'> {
  brief: Brief;
  buttons?: React.ReactNode;
  footer?: React.ReactNode;
  customDisclaimer?: React.ReactNode;
  shouldShowSidebar?: boolean;
  withDivider?: boolean;
  title?: React.ReactNode;
  allowSaveAfterEditOnly?: boolean;
  onSectionEditStateChange?: (hasOpenSection: boolean) => void;
  enableScrollableHeader?: boolean;
}

type BriefEditorContentProps = Pick<
  BriefEditorProps,
  'buttons' | 'footer' | 'customDisclaimer' | 'shouldShowSidebar' | 'withDivider' | 'title' | 'enableScrollableHeader'
> & {
  onClose: () => void;
};

const BriefEditorContent = ({
  buttons,
  footer,
  customDisclaimer,
  withDivider = true,
  shouldShowSidebar,
  title,
  onClose,
  onSectionEditStateChange,
  enableScrollableHeader,
}: BriefEditorContentProps & {
  onSectionEditStateChange?: (hasOpenSection: boolean) => void;
}) => {
  const { editedBrief, editedSectionId } = useBriefContext();
  const { isMobile } = getContext();

  const [includedSections, excludedSections] = useMemo(
    () => partition(editedBrief?.sections ?? [], (section) => section.included),
    [editedBrief?.sections]
  );

  useEffect(() => {
    onSectionEditStateChange?.(Boolean(editedSectionId));
  }, [editedSectionId, onSectionEditStateChange]);

  const shouldDisableSidebar = !!editedSectionId;
  const shouldHideFooter = isMobile && shouldDisableSidebar;

  return (
    <Layout
      title={title}
      onClose={onClose}
      containerPaddingXByBreakpoint={containerPaddingXBySmBreakpoint}
      withDividerUnderSubTitle={false}
      withDivider={withDivider}
      withTitle={title !== undefined}
      applyContainerPaddingOnContent={false}
      enableScrollableHeader={enableScrollableHeader}
      content={
        <Stack height="100%" width="100%" overflowY="hidden" gap="0" withDivider>
          {shouldShowSidebar && <ExcludedSectionsSidebar sections={excludedSections} disabled={shouldDisableSidebar} />}
          <IncludedSections
            sections={includedSections}
            editedSectionId={editedSectionId}
            customDisclaimer={customDisclaimer}
          />
        </Stack>
      }
      footer={
        shouldHideFooter ? undefined : (
          <Stack direction="column" gap="0">
            <ExcludedSectionsNavBar
              sections={excludedSections}
              disabled={shouldDisableSidebar}
              shouldShowSidebar={shouldShowSidebar}
            />
            {footer}
          </Stack>
        )
      }
      buttons={shouldHideFooter ? undefined : buttons}
    />
  );
};

export const BriefEditor = ({
  onBriefChanged,
  onClose,
  subCategoryId,
  categoryId,
  source,
  seller,
  componentName,
  brief,
  buttons,
  footer,
  customDisclaimer,
  withDivider = true,
  shouldShowSidebar,
  title,
  allowSaveAfterEditOnly,
  onSectionEditStateChange,
  enableScrollableHeader = false,
}: BriefEditorProps) => (
  <QueryProvider>
    <AppProvider
      onClose={onClose}
      isBriefDrawerOpen={true}
      subCategoryId={subCategoryId}
      categoryId={categoryId}
      source={source}
      seller={seller}
      componentName={componentName}
      brief={brief}
      onBriefChanged={onBriefChanged}
      allowSaveAfterEditOnly={allowSaveAfterEditOnly}
    >
      <BriefEditorContent
        buttons={buttons}
        footer={footer}
        customDisclaimer={customDisclaimer}
        withDivider={withDivider}
        shouldShowSidebar={shouldShowSidebar}
        title={title}
        onClose={onClose}
        onSectionEditStateChange={onSectionEditStateChange}
        enableScrollableHeader={enableScrollableHeader}
      />
    </AppProvider>
  </QueryProvider>
);
